/**
 * What a character may be, as the server publishes it.
 *
 * **The client never decides any of this.** Every constraint here came from
 * the corpus and is checked again on `POST /api/characters` — so what these
 * functions produce is guidance: which options to grey out and what to say
 * about them, in a wizard where the alternative is a player filling in six
 * steps and being refused at the end.
 */

export interface RaceOption {
  race: string;
  adjustments: Record<string, number>;
  /** Min/max **as rolled**, before the racial adjustment above is applied. */
  requirements: Record<string, [number, number]>;
  classes: { class: string; limit: number | string }[];
}

export interface ClassOption {
  class: string;
  requirements: Record<string, number>;
  /** Printed prose: `"any"`, `"Lawful Good"`, `"any except lawful good"`. */
  alignment: string;
  hitDie: number;
  /** Table 5.1: what this class has to spend at 1st level. */
  combatSkillPoints?: number;
  /** What swinging an untrained weapon costs it, and a same-group one. */
  unskilledPenalty?: { unrelated: number; sameGroup: number };
  /** Table 5.1's row: `warrior`, `priestOrRogue` or `wizard`. */
  combatSkillGroup?: string;
}

/**
 * What a Combat Skill Point may be exchanged for, as the server publishes it.
 *
 * ⚠ **Served, never copied here.** The costs are printed prose, the groups are
 * Table 5.2, and which methods a class may learn is three sentences that do not
 * compose the way they read — so a client-side copy would offer a purchase the
 * server refuses, or worse, one it accepts for the wrong price.
 */
export interface CombatSkillCatalogue {
  tiers: { tier: string; cost: number; effect: string }[];
  groups: Record<string, string[]>;
  damageTypes: string[];
  methods: { name: string; category: 'melee' | 'ranged' | 'unarmed'; cost: number }[];
  methodRules: Record<string, string>;
  ambidexterity: { cost: number; eligibility: string; effect: string };
  /** "…those not exchanged during character creation are lost." */
  rule: string;
  ungroupedWeapons: string;
  optionalRule: string;
  weapons: string[];
  /** What specialising in each weapon costs — a rule, so it is served. */
  specialisationCost?: Record<string, number>;
}

/** One race's Table 2.5 and Table 2.6 rows, as the server publishes them. */
export interface BodyOption {
  race: string;
  heightWeight: {
    /** Base inches by printed column — `male`, `female`. */
    baseHeightIn: Record<string, number>;
    heightModifier: { count: number; sides: number };
    baseWeightLb: Record<string, number>;
    weightModifier: { count: number; sides: number };
  };
  age: {
    startingAge: number;
    startingVariable: { count: number; sides: number };
    startingRange: { min: number; max: number };
    /** ⚠ Published and never rolled — the GM rolls it secretly, and nothing
     *  here ages a character. It is what an entered age is bounded by. */
    maxAgeFormula: { printed: string; base: number; min: number; max: number };
    /** Middle, old and venerable, with what each **cumulatively** costs. */
    steps: {
      step: string;
      beginsAt: number;
      printed: string;
      adjustments: Record<string, number>;
    }[];
  };
}

export interface CharacterOptions {
  abilities: string[];
  alignments: string[];
  /**
   * `points` is the budget method — it hands out points, it does not roll.
   * `pointsBounds` and `exceptionalStrengthPercentPerPoint` are parsed
   * server-side out of the method's own printed sentence and are present only
   * on that one method — see `generationMethods()` in the rules-data package.
   */
  generationMethods: {
    key: string;
    /** The book's own name, e.g. "Method IV — Points System" — show this in a picker, not `description`. */
    label: string;
    description: string;
    kind?: 'roll' | 'points';
    /** "assign as player chooses" (Methods II, III) vs. a fixed printed order (Method I). */
    assignPlayerChooses?: boolean;
    pointsBounds?: { min: number; max: number };
    exceptionalStrengthPercentPerPoint?: number;
  }[];
  races: RaceOption[];
  classes: ClassOption[];
  /**
   * The schools a specialist mage may choose.
   *
   * Served, not listed here: not every printed school qualifies — Lesser
   * Divination is castable by any wizard and names no specialist — and a copy
   * in the client would offer a specialty the book does not have.
   */
  specialistSchools?: string[];
  /** The books a mage may keep, with their capacity and what a page costs to copy into. */
  spellbooks?: { type: string; pages: number; costPerPage: string }[];
  /**
   * Bonus languages: the printed pool per race, the sentence distinguishing
   * a demi-human's restricted pool from a human's open one, and the budget
   * `ability-scores.json → intelligence` grants per score.
   *
   * Served, not derived here: which languages are pickable is a table per
   * race, and the budget is a banded column the client would otherwise have
   * to re-parse.
   */
  bonusLanguages?: {
    rule: string;
    pools: Record<string, string[]>;
    budgetByIntelligence: Record<string, number>;
  };
  /**
   * The two headings Table 2.5 prints its base height and weight under.
   *
   * Served, never listed here: which printed row a height is rolled off is the
   * book's, and a client-side copy would be inventing a column. It is **not**
   * the list of genders a character may have — that is free text, and the two
   * are separate questions for exactly this reason.
   */
  bodyColumns?: string[];
  /** Tables 2.5 and 2.6 per race, so the wizard can show what it will roll. */
  bodies?: BodyOption[];
  /** Chapter 5 — what a Combat Skill Point buys. */
  combatSkills?: CombatSkillCatalogue;
  /**
   * The trades a zero-level NPC can be framed as.
   *
   * Served, never listed here: a soldier is `1d8+1` and a scholar `1d3`, and a
   * client-side copy would offer a profession the engine refuses by name.
   */
  zeroLevelProfessions?: string[];
  /**
   * Every non-combat skill, with what it costs and what it is rolled against.
   *
   * Served for the same reasons as everything above, and one sharper: the
   * class column is a **restriction** and the tiers are worth +3 and +6 to
   * every check they touch, so a client-side copy would offer a skill the
   * server refuses and price it wrongly while doing so.
   */
  skills?: {
    name: string;
    family: string;
    classGroups: string[];
    /** Absent means the book prints no ability for it: not a check at all. */
    ability?: string;
    difficulty?: number;
    cost?: number;
    untrainedAllowed?: boolean;
    summary?: string;
  }[];
  /** What each tier is worth, the situational range, and what aid adds. */
  skillRules?: {
    tiers: Record<string, number>;
    situational: { min: number; max: number };
    aiding: { helps: number; hinders: number };
    whenToCheck: string;
    groupChecks: string;
    startingBonusSkill: string;
    startingBonusFamilies: string[];
  };
}

export type Scores = Record<string, number>;

/**
 * The scores after the racial adjustment.
 *
 * Kept apart from the rolled ones on purpose: a race is qualified for on what
 * was **rolled** and a class on what the character **is**, and a wizard that
 * held one number for both gets one of the two wrong.
 */
export function adjustedScores(rolled: Scores, race: RaceOption | undefined): Scores {
  if (!race) return { ...rolled };
  const out: Scores = { ...rolled };
  for (const [ability, delta] of Object.entries(race.adjustments)) {
    out[ability] = (out[ability] ?? 0) + delta;
  }
  return out;
}

/** Why this race is not available for these rolled scores, if it is not. */
export function raceBlockers(race: RaceOption, rolled: Scores): string[] {
  const blockers: string[] = [];
  for (const [ability, [min, max]] of Object.entries(race.requirements)) {
    const score = rolled[ability];
    if (score === undefined) continue;
    if (score < min || score > max) {
      blockers.push(`${ability} ${score} is outside ${min}–${max} as rolled`);
    }
  }
  return blockers;
}

/**
 * Why this class is not available, given the race and the adjusted scores.
 *
 * `adjusted` is `null` when there are no scores yet to check — the class step
 * now comes before the scores step, and a class picked there has nothing to
 * be adjusted against. The **forbidden-by-race** door is not a score check at
 * all, so it still shuts in that case; only the ability requirements wait for
 * a roll or an allocation to exist.
 */
export function classBlockers(
  option: ClassOption,
  race: RaceOption | undefined,
  adjusted: Scores | null,
): string[] {
  const blockers: string[] = [];

  if (adjusted) {
    for (const [ability, needed] of Object.entries(option.requirements)) {
      const score = adjusted[ability];
      if (score !== undefined && score < needed) {
        blockers.push(`needs ${ability} ${needed}+, has ${score}`);
      }
    }
  }

  const limit = race?.classes.find((c) => c.class === option.class)?.limit;
  // "forbidden" is published rather than omitted, so the door can be shown
  // shut with a reason instead of leaving a gap in the list.
  if (limit === 'forbidden') blockers.push(`a ${race?.race} cannot be a ${option.class}`);

  return blockers;
}

/**
 * Which alignments a class allows, from the printed prose.
 *
 * The same reading the server does, and it is *only* a hint: the server
 * decides, and a rule this cannot parse leaves every alignment offered rather
 * than refusing a legal character on a sentence it did not understand.
 */
export function allowedAlignments(rule: string, alignments: readonly string[]): string[] {
  const demanded = rule.trim().toLowerCase();
  if (demanded === '' || demanded === 'any') return [...alignments];

  const spelled = (alignment: string) =>
    alignment.replace(/([A-Z])/g, ' $1').toLowerCase().trim();

  const except = /^any except (.+)$/.exec(demanded);
  if (except?.[1]) {
    const banned = except[1].split(/,| or /).map((part) => part.trim());
    return alignments.filter((a) => !banned.includes(spelled(a)));
  }

  const distributed = /^(.+?)\s+(good|evil|neutral)$/.exec(demanded);
  if (distributed?.[1] && distributed[2] && /,| or /.test(distributed[1])) {
    const options = distributed[1]
      .split(/,| or /)
      .map((prefix) => `${prefix.trim()} ${distributed[2]}`);
    const matched = alignments.filter(
      (a) => options.includes(spelled(a)) || (options.includes('true neutral') && spelled(a) === 'true neutral'),
    );
    return matched.length > 0 ? matched : [...alignments];
  }

  const listed = demanded.split(/,| or /).map((part) => part.trim());
  const matched = alignments.filter((a) => listed.includes(spelled(a)));
  return matched.length > 0 ? matched : [...alignments];
}

/**
 * How many bonus languages this Intelligence grants, from the served table.
 *
 * `budgetByIntelligence` is keyed by the score as a string (it crossed a JSON
 * response), so this is the one place that does the lookup rather than every
 * caller remembering to `String()` its own key.
 */
export function bonusLanguageBudget(
  options: CharacterOptions | undefined,
  intelligence: number,
): number {
  return options?.bonusLanguages?.budgetByIntelligence[String(intelligence)] ?? 0;
}

/** Which languages a bonus pick may come from, for this race. */
export function bonusLanguagePoolFor(
  options: CharacterOptions | undefined,
  race: string,
): string[] {
  return options?.bonusLanguages?.pools[race] ?? [];
}

/** `halfElf` → `Half elf`, `specialistMage` → `Specialist mage`. */
export function optionLabel(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * What the roll says about itself when the server had to throw sets away to find one that qualifies for the
 * chosen race and class. This is this application's rule rather than the book's (it changes the odds of what a
 * character is), so it is stated whenever it did anything.
 */
export function rerollNote(rerolls: number, race: string, characterClass: string): string {
  const who = [race, characterClass].filter((word) => word !== '').map(optionLabel).join(' ').toLowerCase();
  return rerolls === 0
    ? `Rolled for a ${who}: the first set qualified.`
    : `Rolled for a ${who}: ${rerolls} ${rerolls === 1 ? 'set was' : 'sets were'} thrown away because ${rerolls === 1 ? 'it' : 'they'} could not qualify (this table's rule, not the book's).`;
}
